import { addSeconds } from 'date-fns'
import appConstants from '~/libs/constants/application-constants.js'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import env from '~/libs/env'

const { nuxtBaseUrl } = appConstants

export const trackingCookies = [
  'capturepage',
  'landing_page',
  'referral_url',
  'affiliate',
  'medium',
  'source',
  'ref',
  'campaign',
  'subid',
  'skipMarketingRecord',
  'adgroup',
  'term',
  'keywords',
  'interest',
]

/**
 * @param {*} numDays Number of days you want to get the expiration date for
 * @returns Date in the future numDays out
 */
const calcExpirationDate = (numDays) => {
  const today = new Date()
  const daysTilCookieExpires = numDays
  const secondsInADay = 86400
  const expirationDate = addSeconds(today, daysTilCookieExpires * secondsInADay)
  return expirationDate
}

/**
 * Class uses nuxt context to serve and retrieve cookies on both client and server
 * depends on the `cookie-universal` node module.
 */
export class NuxtCookieService {
  constructor ($cookies, cookiesDomain = '.lendio.com') {
    if (!$cookies) {
      throw new Error('NuxtCookieService requires $cookies object on instantiation')
    }
    this.$cookies = $cookies

    const defaultOptions = {
      path: `${nuxtBaseUrl}/`,
      domain: cookiesDomain,
      expires: 7, // Default, cookie expires after 7 days
      secure: true,
      sameSite: 'None'
    }
    this.defaultOptions = {
      ...defaultOptions,
    }

    this.trackingCookies = trackingCookies

    this._responseCookies = {}
  }

  getTrackingCookies () {
    var cookies = {}
    this.trackingCookies.forEach((cookie) => {
      const value = this.get(cookie)
      if (value && !/^(null|undefined|NaN)$/i.exec(value)) {
        cookies[cookie] = value
      }
    })
    return cookies
  }

  clearTrackingCookies () {
    const excludedCookies = ['dreamsActive']
    this.trackingCookies.filter(cookie => !excludedCookies.includes(cookie))
      .forEach((cookie) => this.remove(cookie))
  }

  setCookiesFromParams (params, options = {}) {
    this.trackingCookies.forEach(cookie => {
      if (Object.keys(params).some((k) => k.includes(`utm_${cookie}`))) {
        params[cookie] = params[`utm_${cookie}`]
      }
      if (params[cookie]) {
        this.set(cookie, params[cookie], options)
      }
    })
  }

  /**
   * @param {*} key string | Cookie name
   * @param {*} opts object |
   *    fromRes: Get cookies from res instead of req.
   *    parseJSON: Parse json, true by default unless overridden globally or locally.
   * @returns Cookie Value
   */
  get(key, opts = {}) {
    if (!opts?.tenantAgnostic) {
      key = this._prependTenantId(key)
    }
    let value = undefined

    return this.$cookies.get(key)
  }

  set (key, value, opts = {}) {
    if (!opts?.tenantAgnostic) {
      key = this._prependTenantId(key)
    }
    const secure = ['LIVE', 'staging', 'preview'].includes(env('environment'))
    const options = {
      ...this.defaultOptions,
      ...(secure ? { sameSite: 'None', secure: true } : { secure: false }),
      ...opts,
    }

    // infer cookie domain and don't use options.domain when using localhost for dev
    if (location.hostname.includes('localhost')) {
      delete options['domain']
    }

    // If a number is passed for expires, set it in days
    if (typeof options['expires'] === 'number') {
      options['expires'] = calcExpirationDate(options['expires'])
    }

    // Defaults to expires, but if maxAge is passed, remove expires
    if (opts.hasOwnProperty('maxAge')) {
      delete options['expires']
    }
    if (options.hasOwnProperty(['expires']) && !(options['expires'] instanceof Date)) {
      options['maxAge'] = 60 * 60 * 24 * options['expires']
      delete options['expires']
    }

    if (!options['secure']) {
      delete options['sameSite']
    }

    return this.$cookies.set(key, value, options)
  }

  removeAggressively(key, opts = {}) {
    key = this._prependTenantId(key)
    const defaultDeleteOptions = { path: this.defaultOptions['path'], domain: this.defaultOptions['domain'] }
    this.$cookies.remove(key, {domain: '.lendio.com', path: `${nuxtBaseUrl}/`})
    this.$cookies.remove(key, {domain: '.lendio.com', path: '/'}) // legacy root-path cookies from before /bp migration
    this.$cookies.remove(key, {domain: '.lendio.com'})
    this.$cookies.remove(key, {}) // in case no options were passed when set
    this.$cookies.remove(key, opts)
    return this.$cookies.remove(key, defaultDeleteOptions)
  }

  remove(key, opts = {}) {
    key = this._prependTenantId(key)
    const secure = ['LIVE', 'staging', 'preview'].includes(env('environment'))

    opts = {
      ...this.defaultOptions,
      ...(secure ? { sameSite: 'None', secure: true } : { secure: false }),
      ...opts, // keep the caller's path, e.g. /bp/embedded, which the fallbacks below never try
      maxAge: -1,
    }

    if (!opts['secure']) {
      delete opts['sameSite']
    }

    if (opts.hasOwnProperty('maxAge')) {
      delete opts['expires']
    }

    return this.removeAggressively(key, opts)
  }

  delete(key, options = {}) {
    key = this._prependTenantId(key)
    return this.remove(key, options)
  }

  addResponseHeaders() {
    Object.values(this._responseCookies).forEach((cookie) => {
      if (cookie.value !== null) {
        this.$cookies.set(cookie.key, cookie.value, cookie.options)
      } else {
        this.$cookies.remove(cookie.key, cookie.options)
      }
    })
  }

  _prependTenantId(key) {
    if (key === 'tenantId') {
      return key
    }

    const tenantId = this._getTenantId()

    if (tenantId !== 1 && !key.startsWith(tenantId)) {
      key = `${tenantId}-${key}`
    }

    return key
  }

  _getTenantId() {
    return useAffiliateCustomizationStore().tenantId
  }

  _responseCookieSet(key, value, options) {
    this._responseCookies[key] = { key, value, options }
  }

  _responseCookieGet(key) {
    return this._responseCookies[key]?.value
  }

  _responseCookieRemove(key, options) {
    this._responseCookies[key] = { key, value: null, options }
  }
}

export default NuxtCookieService
